// Modal de dados de cobrança (CPF/CNPJ + telefone) — portado do Reservei_Web
// (`src/components/checkout/BillingInfoModal.tsx`).
//
// Adaptação CC: o checkout é público (sem usuário logado), então o modal NÃO
// salva nada no backend — só valida e devolve os valores limpos via `onSaved`.
// Eles entram no payload do POST /api/public/checkout. Visual preservado.

import { AnimatePresence, motion } from 'framer-motion';
import { ShieldCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { isValidCpfOrCnpj, normalizeDocument } from '@control-center/shared';

import { maskCpfCnpjAuto } from '../../lib/masks';

import { cn } from '@control-center/ui';

interface BillingInfoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Callback chamado depois que o user preencheu dados válidos.
   * Recebe CPF/CNPJ e telefone já sem máscara (só dígitos).
   * Usado pra retomar a operação que disparou o modal (gerar PIX, etc).
   */
  onSaved: (cpfCnpj: string, phone: string) => void;
  /** Valores iniciais (quando o user reabre o modal). */
  initialCpfCnpj?: string;
  initialPhone?: string;
}

// ========================================
// Máscara de telefone local — CPF/CNPJ usa o helper central (alfanumérico DR-012)
// ========================================
function maskPhone(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 10) {
    return digits.replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{4})(\d{1,4})$/, '$1-$2');
  }
  return digits.replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d{1,4})$/, '$1-$2');
}

export function BillingInfoModal({
  open,
  onOpenChange,
  onSaved,
  initialCpfCnpj = '',
  initialPhone = '',
}: BillingInfoModalProps): JSX.Element {
  const [cpfCnpj, setCpfCnpj] = useState('');
  const [phone, setPhone] = useState('');
  const [cpfError, setCpfError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);

  // Reset ao abrir
  useEffect(() => {
    if (open) {
      setCpfCnpj(maskCpfCnpjAuto(initialCpfCnpj));
      setPhone(maskPhone(initialPhone));
      setCpfError(null);
      setPhoneError(null);
    }
  }, [open, initialCpfCnpj, initialPhone]);

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();

    // Validação client-side (UX) — backend revalida (segurança)
    const cleanCpf = normalizeDocument(cpfCnpj);
    if (!isValidCpfOrCnpj(cleanCpf)) {
      setCpfError('CPF ou CNPJ inválido. Verifique os dígitos.');
      return;
    }
    setCpfError(null);

    const cleanPhone = phone.replace(/\D/g, '');
    if (cleanPhone.length > 0 && (cleanPhone.length < 10 || cleanPhone.length > 11)) {
      setPhoneError('Telefone deve ter 10 ou 11 dígitos.');
      return;
    }
    setPhoneError(null);

    onOpenChange(false);
    // Pequeno delay pra modal fechar antes de retomar (mesmo padrão do original)
    setTimeout(() => onSaved(cleanCpf, cleanPhone), 200);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[100] flex items-end justify-center bg-black/40 p-4 backdrop-blur-sm sm:items-center"
          onClick={() => onOpenChange(false)}
          role="presentation"
        >
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            // [color-scheme:light] é obrigatório: o app é dark-first (globals.css
            // define color-scheme:dark no html), o que faz o browser pintar os
            // controles nativos (input/autofill) com a paleta escura. Numa superfície
            // branca isso deixava o texto escuro ilegível sobre fundo escuro.
            className="w-full max-w-[440px] max-h-[90dvh] overflow-y-auto rounded-[16px] bg-white shadow-[0_8px_24px_rgba(0,0,0,0.12)] [color-scheme:light]"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Dados para pagamento"
          >
            {/* Header */}
            <div className="border-b border-black/[0.06] bg-gradient-to-br from-teal-50 to-white px-6 pb-4 pt-6">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-[10px] bg-teal-100">
                  <ShieldCheck className="h-5 w-5 text-teal-700" />
                </div>
                <div>
                  <h2 className="text-[17px] font-bold leading-tight text-slate-900">
                    Dados para pagamento
                  </h2>
                  <p className="mt-1 text-[13px] text-slate-500">
                    Precisamos do seu CPF/CNPJ para gerar a cobrança no nosso provedor (Asaas).
                  </p>
                </div>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4 px-6 py-5">
              <div>
                <label
                  htmlFor="checkout-cpfCnpj"
                  className="text-[12px] font-semibold uppercase tracking-wider text-slate-700"
                >
                  CPF ou CNPJ
                </label>
                <input
                  id="checkout-cpfCnpj"
                  type="text"
                  inputMode="text"
                  autoCapitalize="characters"
                  autoComplete="off"
                  placeholder="000.000.000-00"
                  value={cpfCnpj}
                  onChange={(e) => {
                    setCpfCnpj(maskCpfCnpjAuto(e.target.value));
                    if (cpfError) setCpfError(null);
                  }}
                  className={cn(
                    'mt-2 h-11 w-full rounded-[10px] border border-slate-200 bg-white px-3 text-[15px] text-slate-900 transition-shadow duration-200 ease-[cubic-bezier(0.19,1,0.22,1)] placeholder:text-slate-400 focus:outline-none focus-visible:shadow-[0_0_0_4px_hsla(187,55%,42%,0.2)]',
                    cpfError &&
                      'border-red-500 focus-visible:shadow-[0_0_0_4px_hsla(0,55%,55%,0.2)]',
                  )}
                  // Modal de input único — foco direto é o esperado (paridade com o original)
                  autoFocus
                />
                {cpfError && (
                  <motion.p
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, ease: [0.19, 1, 0.22, 1] }}
                    className="mt-1.5 text-[12px] text-red-600"
                  >
                    {cpfError}
                  </motion.p>
                )}
              </div>

              <div>
                <label
                  htmlFor="checkout-phone"
                  className="text-[12px] font-semibold uppercase tracking-wider text-slate-700"
                >
                  Telefone (opcional)
                </label>
                <input
                  id="checkout-phone"
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel"
                  placeholder="(11) 99999-9999"
                  value={phone}
                  onChange={(e) => {
                    setPhone(maskPhone(e.target.value));
                    if (phoneError) setPhoneError(null);
                  }}
                  className={cn(
                    'mt-2 h-11 w-full rounded-[10px] border border-slate-200 bg-white px-3 text-[15px] text-slate-900 transition-shadow duration-200 ease-[cubic-bezier(0.19,1,0.22,1)] placeholder:text-slate-400 focus:outline-none focus-visible:shadow-[0_0_0_4px_hsla(187,55%,42%,0.2)]',
                    phoneError &&
                      'border-red-500 focus-visible:shadow-[0_0_0_4px_hsla(0,55%,55%,0.2)]',
                  )}
                />
                {phoneError && (
                  <motion.p
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, ease: [0.19, 1, 0.22, 1] }}
                    className="mt-1.5 text-[12px] text-red-600"
                  >
                    {phoneError}
                  </motion.p>
                )}
              </div>

              {/* Trust signal */}
              <div className="flex items-center gap-2 pt-1 text-[11px] text-slate-500">
                <ShieldCheck className="h-3.5 w-3.5 flex-shrink-0 text-teal-600" />
                <span>
                  Seus dados são criptografados e usados apenas para processar pagamentos.
                </span>
              </div>

              {/* CTA */}
              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => onOpenChange(false)}
                  className="h-11 flex-1 cursor-pointer rounded-[10px] border border-slate-200 text-[14px] font-medium text-slate-700 transition-all duration-200 ease-[cubic-bezier(0.19,1,0.22,1)] hover:bg-slate-50 focus-visible:outline-none focus-visible:shadow-[0_0_0_4px_hsla(187,55%,42%,0.2)] active:scale-[0.97]"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={!cpfCnpj}
                  className={cn(
                    'h-11 flex-1 rounded-[10px] text-[14px] font-semibold text-white transition-all duration-200 ease-[cubic-bezier(0.19,1,0.22,1)] focus-visible:outline-none focus-visible:shadow-[0_0_0_4px_hsla(187,55%,42%,0.2)]',
                    cpfCnpj
                      ? 'cursor-pointer bg-gradient-to-r from-teal-600 to-teal-500 shadow-[0_1px_2px_rgba(0,0,0,0.05)] hover:from-teal-700 hover:to-teal-600 hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)] active:scale-[0.97]'
                      : 'cursor-not-allowed bg-slate-300',
                  )}
                >
                  Salvar e continuar
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
